import { useCallback, useState } from "react";

import type {
  UseFilterPaginationProps,
  UseFilterPaginationReturn,
} from "./use-filter-pagination";
import { useFilterPagination } from "./use-filter-pagination";

export interface UseFilterPaginationWithBooleanProps<T>
  extends UseFilterPaginationProps<T> {
  initialBooleanFilter?: boolean;
  booleanFilterKey?: string;
}

export interface UseFilterPaginationWithBooleanReturn<T>
  extends UseFilterPaginationReturn<T> {
  booleanFilter: boolean;
  handleBooleanFilterChange: (value: boolean) => void;
  getCombinedFilter: () => string;
}

export const useFilterPaginationWithBoolean = <T>({
  initialContent,
  fetchFunction,
  initialFilter = "all",
  initialBooleanFilter = false,
  booleanFilterKey = "onResort",
}: UseFilterPaginationWithBooleanProps<T>): UseFilterPaginationWithBooleanReturn<T> => {
  const [booleanFilter, setBooleanFilter] = useState(initialBooleanFilter);

  const baseHook = useFilterPagination({
    initialContent,
    fetchFunction,
    initialFilter,
  });

  const handleBooleanFilterChange = useCallback(
    (value: boolean) => {
      setBooleanFilter(value);

      // Extract the base filter without the boolean part
      const baseFilter = baseHook.activeFilter
        .replace(`&${booleanFilterKey}=true`, "")
        .replace(booleanFilterKey, "all");

      // Calculate the combined filter immediately and trigger refetch
      const combinedFilter = value
        ? baseFilter === "all"
          ? booleanFilterKey
          : `${baseFilter}&${booleanFilterKey}=true`
        : baseFilter;

      baseHook.handleChangeFilter(combinedFilter);
    },
    [baseHook.activeFilter, baseHook.handleChangeFilter, booleanFilterKey],
  );

  // Override handleChangeFilter to preserve boolean filter state
  const handleChangeFilter = useCallback(
    (filter: string) => {
      // Combine the new base filter with the current boolean filter state
      const combinedFilter = booleanFilter
        ? filter === "all"
          ? booleanFilterKey
          : `${filter}&${booleanFilterKey}=true`
        : filter;

      baseHook.handleChangeFilter(combinedFilter);
    },
    [baseHook.handleChangeFilter, booleanFilter, booleanFilterKey],
  );

  const getCombinedFilter = useCallback(() => {
    const baseFilter = baseHook.activeFilter;
    if (booleanFilter) {
      return baseFilter === "all"
        ? booleanFilterKey
        : `${baseFilter}&${booleanFilterKey}=true`;
    }
    return baseFilter;
  }, [baseHook.activeFilter, booleanFilter, booleanFilterKey]);

  return {
    ...baseHook,
    handleChangeFilter, // Override with our version that preserves boolean filter
    booleanFilter,
    handleBooleanFilterChange,
    getCombinedFilter,
  };
};
